import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FiActivity,
  FiArrowLeft,
  FiArrowRight,
  FiBox,
  FiCheck,
  FiChevronDown,
  FiCpu,
  FiHeart,
  FiLayers,
  FiMinus,
  FiMonitor,
  FiPlus,
  FiShield,
  FiTruck,
  FiZap,
} from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";
import "../css/productdetails.scss";

const detailSections = [
  {
    id: "description",
    title: "DESCRIPTION + SPECS",
  },
  {
    id: "box",
    title: "WHAT'S IN THE BOX",
  },
  {
    id: "shipping",
    title: "SHIPPING + WARRANTY",
  },
];

const reviewSamples = [
  {
    name: "Jordan M.",
    title: "The sound is exactly right",
    body: "Solid without feeling heavy, and the finish looks even better on the desk. Setup took less than a minute.",
  },
  {
    name: "Riley S.",
    title: "Premium from the first input",
    body: "The controls feel immediate and consistent. Packaging was thoughtful and everything arrived in perfect condition.",
  },
];

const getFeatureContent = (product) => {
  if (product.category === "Keyboards") {
    return [
      { icon: FiZap, title: product.tags.includes("hall-effect") ? "DYNAMIC ACTUATION" : "HOT-SWAP CONTROL", text: product.tags.includes("hall-effect") ? "Set your actuation point for movement that responds exactly when you need it." : "Change compatible switches without soldering and tune every row to your preferred feel." },
      { icon: FiLayers, title: "WEIGHTED FEEL + THOCK", text: "Layered internal dampening reduces case resonance for a focused, substantial sound." },
      { icon: FiCpu, title: "PROGRAMMABLE PERFORMANCE", text: "Remap inputs, lighting, and shortcuts to move effortlessly between play and work." },
      { icon: FiMonitor, title: "MAC + PC READY", text: "A simple USB-C connection and OS-friendly legends make setup immediate." },
    ];
  }

  if (product.category === "Mice") {
    return [
      { icon: FiActivity, title: "PIXEL-PRECISE SENSOR", text: "A high-resolution optical sensor delivers consistent tracking from flick to fine adjustment." },
      { icon: FiZap, title: "LOW-LATENCY WIRELESS", text: "Tournament-ready 2.4 GHz response without a cable across your workspace." },
      { icon: FiBox, title: "BALANCED SHELL", text: "A carefully shaped, lightweight body supports confident control through long sessions." },
      { icon: FiShield, title: "BUILT FOR DAILY USE", text: "Durable switches and a resilient finish stand up to repeated competitive inputs." },
    ];
  }

  return [
    { icon: FiLayers, title: "CONSIDERED MATERIAL", text: "Selected surfaces and structural layers balance comfort, durability, and control." },
    { icon: FiBox, title: "DESK-READY FIT", text: "Proportions are tuned to pair naturally with compact and full-size setups." },
    { icon: FiShield, title: "MADE TO LAST", text: "Durable finishes and reinforced details withstand repeated daily use." },
    { icon: FiCheck, title: "EASY SETUP", text: "Designed to work immediately with the gear already on your desk." },
  ];
};

const renderAccordionContent = (section, product) => {
  if (section === "description") {
    return (
      <div className="pdp-accordion__specs">
        <p>{product.description}</p>
        <dl>
          {Object.entries(product.attributes).map(([key, value]) => (
            <div key={key}>
              <dt>{key.replace(/([A-Z])/g, " $1")}</dt>
              <dd>{value}</dd>
            </div>
          ))}
          <div><dt>Material</dt><dd>{product.material}</dd></div>
        </dl>
      </div>
    );
  }

  if (section === "box") {
    return (
      <ul>
        <li>{product.name}</li>
        <li>{product.category === "Keyboards" ? "Braided USB-C cable and keycap puller" : "Charging / connection accessories where applicable"}</li>
        <li>Quick-start guide and warranty information</li>
      </ul>
    );
  }

  return (
    <div>
      <p>In-stock products leave our warehouse in 1–3 business days. Tracking is emailed when your order ships.</p>
      <p>Tracked shipping is {formatPrice(siteConfig.commerce.standardShipping)}. Every product includes a one-year limited hardware warranty.</p>
    </div>
  );
};

function ProductDetail() {
  const { id } = useParams();
  const product = productData.find((item) => Number(item.id) === Number(id));
  const { addToCart, isWishlisted, toggleWishlist } = useContext(CartContext);
  const [selectedVariant, setSelectedVariant] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [openSection, setOpenSection] = useState("description");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [added, setAdded] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const purchaseButtonRef = useRef(null);

  useEffect(() => {
    if (!product) return;
    setSelectedVariant(product.size[0]);
    setSelectedColor(product.colors[0]);
    setSelectedImage(0);
    setQuantity(1);
    setAdded(false);
    document.title = `${product.name} | ${siteConfig.wordmark}`;
  }, [product]);

  useEffect(() => {
    const node = purchaseButtonRef.current;
    if (!node || !product || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setShowSticky(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [product]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const sameCategory = productData.filter(
      (item) => item.id !== product.id && item.category === product.category
    );
    const others = productData.filter(
      (item) => item.id !== product.id && item.category !== product.category
    );
    return [...sameCategory, ...others].slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main className="page-error">
        <div>
          <p className="eyebrow">404 / PRODUCT</p>
          <h1 className="section-heading">PRODUCT NOT FOUND</h1>
          <p>This product may have moved or is no longer part of the collection.</p>
          <Link className="site-button site-button--dark" to="/shop">BACK TO SHOP</Link>
        </div>
      </main>
    );
  }

  const discount = product.compareAtPrice
    ? Math.round((1 - product.price / product.compareAtPrice) * 100)
    : 0;
  const features = getFeatureContent(product);

  const addProduct = () => {
    addToCart(product.id, quantity, selectedVariant, selectedColor);
    setAdded(true);
    window.dispatchEvent(new CustomEvent("hushcider:open-cart"));
    window.setTimeout(() => setAdded(false), 2200);
  };

  const changeImage = (direction) => {
    setImageLoaded(false);
    setSelectedImage((current) =>
      (current + direction + product.images.length) % product.images.length
    );
  };

  return (
    <main className="product-detail">
      <nav className="pdp-breadcrumb" aria-label="Breadcrumb">
        <Link to="/">HOME</Link><span>/</span>
        <Link to={`/shop?category=${product.category}`}>{product.category.toUpperCase()}</Link><span>/</span>
        <span>{product.name}</span>
      </nav>

      <section className="pdp-top">
        <div className="pdp-gallery">
          <div className={`pdp-gallery__main ${imageLoaded ? "is-loaded" : ""}`}>
            {discount > 0 && <span className="pdp-gallery__badge">{discount}% OFF</span>}
            <img
              key={product.images[selectedImage]}
              src={product.images[selectedImage]}
              alt={`${product.name} view ${selectedImage + 1}`}
              onLoad={() => setImageLoaded(true)}
            />
            <div className="pdp-gallery__skeleton" aria-hidden="true" />
            <button type="button" onClick={() => changeImage(-1)} aria-label="Previous image"><FiArrowLeft /></button>
            <button type="button" onClick={() => changeImage(1)} aria-label="Next image"><FiArrowRight /></button>
          </div>
          <div className="pdp-gallery__thumbs">
            {product.images.map((image, index) => (
              <button
                type="button"
                key={image}
                className={index === selectedImage ? "is-active" : ""}
                onClick={() => { setImageLoaded(false); setSelectedImage(index); }}
                aria-label={`Show image ${index + 1}`}
              >
                <img src={image} alt="" />
              </button>
            ))}
          </div>
          <div className="pdp-gallery__progress">
            <span style={{ width: `${((selectedImage + 1) / product.images.length) * 100}%` }} />
          </div>
        </div>

        <aside className="pdp-purchase">
          <div className="pdp-rating">
            <span aria-label={`${product.rating} out of 5 stars`}>★★★★★</span>
            <a href="#reviews">{product.rating} · {product.reviews} REVIEWS</a>
          </div>
          <p className="pdp-purchase__collection">{product.collection.toUpperCase()} / {product.category.toUpperCase()}</p>
          <h1>{product.name}</h1>
          <div className="pdp-price">
            <strong>{formatPrice(product.price)}</strong>
            {product.compareAtPrice && <del>{formatPrice(product.compareAtPrice)}</del>}
          </div>
          <p className="pdp-installments">Pay in 4 interest-free payments on eligible orders.</p>

          <div className="pdp-option">
            <div className="pdp-option__head"><span>COLOR</span><strong>{selectedColor}</strong></div>
            <div className="pdp-option__values pdp-option__values--colors">
              {product.colors.map((color) => (
                <button
                  type="button"
                  key={color}
                  className={selectedColor === color ? "is-active" : ""}
                  onClick={() => setSelectedColor(color)}
                >
                  <span style={{ background: color.toLowerCase().includes("ice") || color.toLowerCase().includes("white") ? "#e9eef5" : color.toLowerCase().includes("sage") ? "#a9b49e" : color.toLowerCase().includes("red") || color.toLowerCase().includes("ember") ? "#ef6c2c" : color.toLowerCase().includes("violet") || color.toLowerCase().includes("nightshade") ? "#5f3c99" : "#333" }} />
                  {color}
                </button>
              ))}
            </div>
          </div>

          <div className="pdp-option">
            <div className="pdp-option__head"><span>{product.category === "Keyboards" ? "SWITCH" : "SIZE / FIT"}</span><strong>{selectedVariant}</strong></div>
            <div className="pdp-option__values">
              {product.size.map((variant) => (
                <button
                  type="button"
                  key={variant}
                  className={selectedVariant === variant ? "is-active" : ""}
                  onClick={() => setSelectedVariant(variant)}
                >
                  {variant}
                </button>
              ))}
            </div>
          </div>

          <div className="pdp-buy-row" ref={purchaseButtonRef}>
            <div className="pdp-quantity" aria-label="Quantity">
              <button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))} aria-label="Decrease quantity"><FiMinus /></button>
              <span>{quantity}</span>
              <button type="button" disabled={quantity >= product.inventory} onClick={() => setQuantity((value) => Math.min(product.inventory, value + 1))} aria-label="Increase quantity"><FiPlus /></button>
            </div>
            <button type="button" className="pdp-add" onClick={addProduct}>
              {added ? <><FiCheck /> ADDED TO BAG</> : `ADD TO BAG — ${formatPrice(product.price * quantity)}`}
            </button>
          </div>

          <button
            type="button"
            className={`pdp-wishlist ${isWishlisted(product.id) ? "is-active" : ""}`}
            onClick={() => toggleWishlist(product.id)}
            aria-pressed={isWishlisted(product.id)}
          >
            <FiHeart fill={isWishlisted(product.id) ? "currentColor" : "none"} />
            {isWishlisted(product.id) ? "SAVED TO WISHLIST" : "ADD TO WISHLIST"}
          </button>

          <div className="pdp-availability">
            <span><FiCheck /> IN STOCK — {product.inventory} AVAILABLE</span>
            <span><FiTruck /> USUALLY SHIPS IN 1–3 BUSINESS DAYS</span>
          </div>

          <p className="pdp-description">{product.description}</p>

          <div className="pdp-accordions">
            {detailSections.map((section) => {
              const isOpen = openSection === section.id;
              return (
                <div key={section.id} className={isOpen ? "is-open" : ""}>
                  <button type="button" onClick={() => setOpenSection(isOpen ? "" : section.id)} aria-expanded={isOpen}>
                    {section.title}<FiChevronDown />
                  </button>
                  {isOpen && <div className="pdp-accordion__content">{renderAccordionContent(section.id, product)}</div>}
                </div>
              );
            })}
          </div>
        </aside>
      </section>

      <section className="pdp-feature-intro">
        <p className="eyebrow">BUILT DIFFERENT</p>
        <h2 className="section-heading">PERFORMANCE YOU CAN FEEL.<br />DESIGN YOU CAN SEE.</h2>
      </section>

      <section className="pdp-features">
        {features.map(({ icon: Icon, title, text }, index) => (
          <article key={title}>
            <span>0{index + 1}</span>
            <Icon />
            <h3>{title}</h3>
            <p>{text}</p>
          </article>
        ))}
      </section>

      <section className="pdp-story-row">
        <div className="pdp-story-row__image">
          <img src={product.images[1]} alt={`${product.name} material detail`} loading="lazy" />
        </div>
        <div className="pdp-story-row__copy">
          <p className="eyebrow">THE FEEL</p>
          <h2 className="section-heading">TUNED FROM<br />THE INSIDE OUT.</h2>
          <p>
            Performance is more than a specification. The structure, surface, and weight of each part are considered together so every input feels deliberate and every session sounds controlled.
          </p>
        </div>
      </section>

      <section className="pdp-spec-wall">
        <div>
          <p className="eyebrow">AT A GLANCE</p>
          <h2 className="section-heading">DETAILS THAT<br />DO THE WORK.</h2>
        </div>
        <dl>
          {Object.entries(product.attributes).map(([key, value]) => (
            <div key={key}><dt>{key.replace(/([A-Z])/g, " $1")}</dt><dd>{value}</dd></div>
          ))}
          <div><dt>Material</dt><dd>{product.material}</dd></div>
          <div><dt>Warranty</dt><dd>1 year limited</dd></div>
        </dl>
      </section>

      <section className="pdp-related">
        <div className="pdp-related__head">
          <h2 className="section-heading">COMPLETE THE SETUP</h2>
          <Link to="/shop">VIEW ALL <FiArrowRight /></Link>
        </div>
        <div className="pdp-related__grid">
          {relatedProducts.map((item) => (
            <article key={item.id}>
              <Link to={`/shopDetails/${item.id}`}>
                <div><img src={item.images[0]} alt={item.name} loading="lazy" /></div>
                <h3>{item.name}</h3>
                <span>{formatPrice(item.price)}</span>
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="pdp-reviews" id="reviews">
        <div className="pdp-reviews__summary">
          <p className="eyebrow">VERIFIED COMMUNITY REVIEWS</p>
          <div><strong>{product.rating}</strong><span>★★★★★</span><small>BASED ON {product.reviews} REVIEWS</small></div>
          <button type="button" className="site-button site-button--dark">WRITE A REVIEW</button>
        </div>
        <div className="pdp-reviews__list">
          {reviewSamples.map((review) => (
            <article key={review.name}>
              <div><span>★★★★★</span><small>VERIFIED BUYER</small></div>
              <h3>{review.title}</h3>
              <p>{review.body}</p>
              <cite>{review.name}</cite>
            </article>
          ))}
        </div>
      </section>

      <div className={`pdp-sticky ${showSticky ? "is-visible" : ""}`}>
        <div><img src={product.images[0]} alt="" /><span>{product.name}</span><strong>{formatPrice(product.price)}</strong></div>
        <button type="button" onClick={addProduct}>ADD TO BAG</button>
      </div>
    </main>
  );
}

export default ProductDetail;
